// Keep camera uploads small enough for mobile connections and proxy limits.
// The server still verifies the JPEG bytes and scopes the upload to the job.
export const PHOTO_UPLOAD_LIMIT = 1_000_000;
const TARGET_BYTES = 900_000;
const MAX_EDGE = 1920;

export class PhotoPreparationError extends Error {}

async function decodePhoto(file: File): Promise<{ width: number; height: number; draw: (canvas: HTMLCanvasElement) => void; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        width: bitmap.width, height: bitmap.height,
        draw: canvas => canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height),
        close: () => bitmap.close(),
      };
    } catch { /* Safari may decode a camera HEIC through <img> instead. */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return {
      width: img.naturalWidth, height: img.naturalHeight,
      draw: canvas => canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height),
      close: () => URL.revokeObjectURL(url),
    };
  } catch {
    URL.revokeObjectURL(url);
    throw new PhotoPreparationError("decode");
  }
}

function jpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(
    blob => blob?.type === "image/jpeg" && blob.size > 0 ? resolve(blob) : reject(new PhotoPreparationError("encode")),
    "image/jpeg", quality,
  ));
}

export async function preparePhoto(file: File): Promise<File> {
  if (file.size > 0 && file.size < PHOTO_UPLOAD_LIMIT &&
    ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"].includes(file.type)) return file;
  if (!file.size) throw new PhotoPreparationError("decode");

  const image = await decodePhoto(file);
  try {
    if (!image.width || !image.height) throw new PhotoPreparationError("decode");
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height));
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new PhotoPreparationError("encode");
    // Transparent library images become a white JPEG, never black.
    for (let attempt = 0; attempt < 7; attempt++) {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      image.draw(canvas);
      for (const quality of [0.82, 0.68, 0.54, 0.42]) {
        const blob = await jpeg(canvas, quality);
        if (blob.size <= TARGET_BYTES) {
          const name = file.name.replace(/\.[^/.]+$/, "") || "installation-photo";
          return new File([blob], `${name}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
        }
      }
      canvas.width = Math.max(1, Math.round(canvas.width * 0.75));
      canvas.height = Math.max(1, Math.round(canvas.height * 0.75));
    }
    throw new PhotoPreparationError("size");
  } finally {
    image.close();
  }
}
